/**
 * Match account detail page.
 *
 * Two-column on desktop: a left-hand "About" card (name, notes, linked party,
 * archive/unarchive) and a right-hand splits editor. On mobile the two stack
 * into a single column. A recent-activity preview table appears below both.
 *
 * The splits editor is the core of the page:
 *   - Add / remove / reorder rows.
 *   - Live sum-of-percentages indicator; remainder = 100 − Σ.
 *   - Local validation (non-empty label, target selected, percentage > 0,
 *     aggregate ≤ 100).
 *   - Server validation (400) surfaces in the banner; 423 routes through the
 *     PIN gate and retries.
 *   - Archived accounts are read-only.
 *
 * Monetary values are rendered as-is from the server (canonical Decimal
 * strings). Percentage math in the live preview uses plain JS numbers — UI
 * math only, never persisted; the server re-validates every write.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowDown,
  ArrowUp,
  ExternalLink,
  ScrollText,
  Trash2,
} from 'lucide-react';
import {
  api,
  ApiError,
  match,
  type MatchAccount,
  type MatchAccountDetail,
  type MatchLedgerRow,
  type MatchSplit,
  type Party,
  type SplitReplaceInput,
} from '../api';
import { MatchShell } from './MatchShell';
import { usePinGate } from '../auth/pinGate';
import {
  Button,
  Card,
  ErrorBanner,
  Pill,
  Select,
  Spinner,
  TextInput,
} from '../ui/primitives';

// Local draft of a split — keyed by a stable random id so React list keys
// survive reorder/delete.
interface DraftSplit {
  key: string;
  targetMatchAccountId: number | null;
  label: string;
  /**
   * The user-typed percentage — kept as string so partial inputs like "7."
   * don't get swallowed by the input. Parsed on render and on submit.
   */
  percentage: string;
}

export function MatchAccountDetailPage() {
  const { id: idParam } = useParams<{ id: string }>();
  const id = Number(idParam);
  const valid = Number.isFinite(id);

  const qc = useQueryClient();
  const nav = useNavigate();
  const { withPin } = usePinGate();

  const detailQ = useQuery<MatchAccountDetail>({
    queryKey: ['match-account', id],
    queryFn: () => match.getAccount(id),
    enabled: valid,
  });

  const recentQ = useQuery<{ rows: MatchLedgerRow[] }>({
    queryKey: ['match-ledger-recent', id],
    queryFn: () => match.getLedger(id, { limit: 10 }),
    enabled: valid,
  });

  const accountsQ = useQuery<MatchAccount[]>({
    queryKey: ['match-accounts', 'active'],
    queryFn: () => match.listAccounts({ status: 'active' }),
  });

  const partiesQ = useQuery<Party[]>({
    queryKey: ['parties', 'all'],
    queryFn: () => api.get<Party[]>('/api/parties'),
  });

  const [pageError, setPageError] = useState<string | null>(null);
  const [savedNotice, setSavedNotice] = useState(false);

  const archiveMut = useMutation({
    mutationFn: () => match.archiveAccount(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['match-account', id] });
      void qc.invalidateQueries({ queryKey: ['match-accounts'] });
      void qc.invalidateQueries({ queryKey: ['match-balances'] });
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 423) return;
      setPageError(e instanceof ApiError ? e.message : String(e));
    },
  });

  const unarchiveMut = useMutation({
    mutationFn: () => match.unarchiveAccount(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['match-account', id] });
      void qc.invalidateQueries({ queryKey: ['match-accounts'] });
      void qc.invalidateQueries({ queryKey: ['match-balances'] });
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 423) return;
      setPageError(e instanceof ApiError ? e.message : String(e));
    },
  });

  if (!valid) {
    return (
      <MatchShell>
        <div className="p-4">
          <ErrorBanner>Not a valid match account id.</ErrorBanner>
        </div>
      </MatchShell>
    );
  }

  if (detailQ.isLoading || !detailQ.data) {
    return (
      <MatchShell>
        <div className="flex items-center gap-2 py-4 text-sm text-ink-muted">
          {detailQ.isError ? (
            <ErrorBanner>
              {detailQ.error instanceof ApiError
                ? detailQ.error.message
                : 'Failed to load match account.'}
            </ErrorBanner>
          ) : (
            <>
              <Spinner /> Loading account…
            </>
          )}
        </div>
      </MatchShell>
    );
  }

  const { account, splits, balance } = detailQ.data;
  const readOnly = account.status === 'archived';
  const linkedParty =
    account.linkedPartyId !== null
      ? partiesQ.data?.find((p) => p.id === account.linkedPartyId)
      : null;
  const mutating = archiveMut.isPending || unarchiveMut.isPending;

  return (
    <MatchShell>
      <div className="space-y-4">
        {/* Breadcrumb + title */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="mb-1 text-xs text-ink-muted">
              <Link
                to="/match/accounts"
                className="hover:text-ink hover:underline"
              >
                ← All match accounts
              </Link>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="truncate text-xl font-semibold text-ink sm:text-2xl">
                {account.name}
              </h1>
              <Pill tone={account.status === 'active' ? 'green' : 'neutral'}>
                {account.status}
              </Pill>
            </div>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-[11px] uppercase tracking-wide text-ink-muted">
                Balance
              </span>
              <span className="font-mono text-2xl text-ink sm:text-3xl">
                {balance}
              </span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => nav(`/match/accounts/${id}/ledger`)}
            >
              <ScrollText className="h-4 w-4" strokeWidth={2} />
              <span>Full ledger</span>
            </Button>
            {account.status === 'active' ? (
              <Button
                size="sm"
                variant="ghost"
                disabled={mutating}
                onClick={() => {
                  setPageError(null);
                  void withPin(() => archiveMut.mutateAsync());
                }}
              >
                {archiveMut.isPending ? <Spinner /> : 'Archive'}
              </Button>
            ) : (
              <Button
                size="sm"
                variant="ghost"
                disabled={mutating}
                onClick={() => {
                  setPageError(null);
                  void withPin(() => unarchiveMut.mutateAsync());
                }}
              >
                {unarchiveMut.isPending ? <Spinner /> : 'Unarchive'}
              </Button>
            )}
          </div>
        </div>

        {pageError && <ErrorBanner>{pageError}</ErrorBanner>}

        {/* Two-column content (about + splits); stacks on mobile. */}
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
          <AboutCard
            account={account}
            linkedParty={linkedParty ?? null}
          />

          <SplitsEditor
            accountId={id}
            readOnly={readOnly}
            initialSplits={splits}
            candidates={(accountsQ.data ?? []).filter((a) => a.id !== id)}
            onSaved={() => {
              setPageError(null);
              setSavedNotice(true);
              setTimeout(() => setSavedNotice(false), 1800);
              void qc.invalidateQueries({ queryKey: ['match-account', id] });
              void qc.invalidateQueries({ queryKey: ['match-balances'] });
            }}
            onError={(msg) => setPageError(msg)}
            withPin={withPin}
            savedNotice={savedNotice}
          />
        </div>

        {/* Recent activity preview */}
        <Card>
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-ink">
              Recent activity
            </h3>
            <Link
              to={`/match/accounts/${id}/ledger`}
              className="text-xs text-brand hover:underline"
            >
              View full ledger →
            </Link>
          </div>
          <RecentActivity
            rows={recentQ.data?.rows ?? []}
            loading={recentQ.isLoading}
          />
        </Card>
      </div>
    </MatchShell>
  );
}

// ---------------------------------------------------------------------------
// About card
// ---------------------------------------------------------------------------

function AboutCard({
  account,
  linkedParty,
}: {
  account: MatchAccount;
  linkedParty: Party | null;
}) {
  return (
    <Card>
      <h3 className="mb-3 text-sm font-semibold text-ink">About</h3>
      <dl className="space-y-3 text-sm">
        <div>
          <dt className="text-[11px] uppercase tracking-wide text-ink-muted">
            Name
          </dt>
          <dd className="mt-0.5 text-ink">{account.name}</dd>
        </div>
        <div>
          <dt className="text-[11px] uppercase tracking-wide text-ink-muted">
            Linked party
          </dt>
          <dd className="mt-0.5">
            {linkedParty ? (
              <Link
                to={`/parties/${linkedParty.id}/ledger`}
                className="inline-flex items-center gap-1 text-brand hover:underline"
              >
                {linkedParty.displayName}
                <span className="text-[10px] uppercase text-ink-muted">
                  {linkedParty.role}
                </span>
                <ExternalLink className="h-3 w-3" strokeWidth={2} />
              </Link>
            ) : (
              <span className="text-ink-muted">—</span>
            )}
          </dd>
        </div>
        <div>
          <dt className="text-[11px] uppercase tracking-wide text-ink-muted">
            Notes
          </dt>
          <dd className="mt-0.5 whitespace-pre-wrap text-sm text-ink">
            {account.notes ?? (
              <span className="text-ink-muted">—</span>
            )}
          </dd>
        </div>
        <div>
          <dt className="text-[11px] uppercase tracking-wide text-ink-muted">
            Created
          </dt>
          <dd className="mt-0.5 font-mono text-xs text-ink-muted">
            {account.createdAt}
          </dd>
        </div>
      </dl>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Splits editor
// ---------------------------------------------------------------------------

function makeKey(): string {
  return Math.random().toString(36).slice(2, 10);
}

function toDraft(s: MatchSplit): DraftSplit {
  return {
    key: makeKey(),
    targetMatchAccountId: s.targetMatchAccountId,
    label: s.label,
    percentage: s.percentage,
  };
}

function SplitsEditor({
  accountId,
  readOnly,
  initialSplits,
  candidates,
  onSaved,
  onError,
  withPin,
  savedNotice,
}: {
  accountId: number;
  readOnly: boolean;
  initialSplits: MatchSplit[];
  candidates: MatchAccount[];
  onSaved: () => void;
  onError: (msg: string) => void;
  withPin: ReturnType<typeof usePinGate>['withPin'];
  savedNotice: boolean;
}) {
  const [drafts, setDrafts] = useState<DraftSplit[]>(() =>
    initialSplits.map(toDraft),
  );
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    setDrafts(initialSplits.map(toDraft));
    setLocalError(null);
  }, [initialSplits]);

  const saveMut = useMutation({
    mutationFn: (payload: SplitReplaceInput) =>
      match.replaceSplits(accountId, payload),
    onSuccess: () => {
      onSaved();
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 423) return; // gate retries
      onError(e instanceof ApiError ? e.message : String(e));
    },
  });

  const sumPct = useMemo(() => {
    let s = 0;
    for (const d of drafts) {
      const n = Number(d.percentage);
      if (Number.isFinite(n)) s += n;
    }
    return Math.round(s * 1e6) / 1e6;
  }, [drafts]);
  const remainder = Math.round((100 - sumPct) * 1e6) / 1e6;
  const overCap = sumPct > 100;

  const addRow = () => {
    setDrafts((prev) => [
      ...prev,
      {
        key: makeKey(),
        targetMatchAccountId: null,
        label: '',
        percentage: '',
      },
    ]);
  };

  const removeRow = (key: string) => {
    setDrafts((prev) => prev.filter((d) => d.key !== key));
  };

  const moveRow = (key: string, dir: -1 | 1) => {
    setDrafts((prev) => {
      const idx = prev.findIndex((d) => d.key === key);
      if (idx < 0) return prev;
      const nextIdx = idx + dir;
      if (nextIdx < 0 || nextIdx >= prev.length) return prev;
      const copy = prev.slice();
      const [item] = copy.splice(idx, 1);
      if (item === undefined) return prev;
      copy.splice(nextIdx, 0, item);
      return copy;
    });
  };

  const updateRow = (key: string, patch: Partial<DraftSplit>) => {
    setDrafts((prev) =>
      prev.map((d) => (d.key === key ? { ...d, ...patch } : d)),
    );
  };

  const onCancel = () => {
    setDrafts(initialSplits.map(toDraft));
    setLocalError(null);
  };

  const onSave = () => {
    setLocalError(null);

    if (readOnly) {
      setLocalError('This account is archived — splits are read-only.');
      return;
    }

    for (const [i, d] of drafts.entries()) {
      if (d.label.trim().length === 0) {
        setLocalError(`Row ${i + 1}: label is required.`);
        return;
      }
      if (d.targetMatchAccountId === null) {
        setLocalError(`Row ${i + 1}: target account is required.`);
        return;
      }
      const n = Number(d.percentage);
      if (!Number.isFinite(n) || n <= 0) {
        setLocalError(`Row ${i + 1}: percentage must be > 0.`);
        return;
      }
      if (n > 100) {
        setLocalError(`Row ${i + 1}: percentage cannot exceed 100.`);
        return;
      }
    }

    if (sumPct > 100) {
      setLocalError(
        `Sum of splits is ${sumPct}% — must be 100% or less.`,
      );
      return;
    }

    const payload: SplitReplaceInput = {
      splits: drafts.map((d) => ({
        targetMatchAccountId: d.targetMatchAccountId as number,
        label: d.label.trim(),
        percentage: String(Number(d.percentage)),
      })),
    };

    void withPin(() => saveMut.mutateAsync(payload));
  };

  const hasChanges = useMemo(() => {
    if (drafts.length !== initialSplits.length) return true;
    for (let i = 0; i < drafts.length; i += 1) {
      const d = drafts[i];
      const s = initialSplits[i];
      if (!d || !s) return true;
      if (
        d.label !== s.label ||
        d.targetMatchAccountId !== s.targetMatchAccountId ||
        Number(d.percentage) !== Number(s.percentage)
      ) {
        return true;
      }
    }
    return false;
  }, [drafts, initialSplits]);

  const targetOptions = [
    { value: '', label: '— select account —' },
    ...candidates.map((a) => ({
      value: String(a.id),
      label: a.name,
    })),
  ];

  return (
    <Card>
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-ink">Splits</h3>
        {savedNotice && (
          <span className="text-[11px] text-pos">saved ✓</span>
        )}
      </div>

      {readOnly && (
        <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
          This account is archived. Splits are read-only. Unarchive to edit.
        </div>
      )}

      {drafts.length === 0 ? (
        <p className="py-6 text-center text-xs text-ink-muted">
          No splits configured. Every entry posts 100% to this account as
          remainder.
        </p>
      ) : (
        <div className="space-y-2">
          {drafts.map((d, i) => (
            <SplitRow
              key={d.key}
              index={i}
              total={drafts.length}
              draft={d}
              targetOptions={targetOptions}
              readOnly={readOnly}
              onChange={(patch) => updateRow(d.key, patch)}
              onRemove={() => removeRow(d.key)}
              onMoveUp={() => moveRow(d.key, -1)}
              onMoveDown={() => moveRow(d.key, 1)}
            />
          ))}
        </div>
      )}

      {/* Live sum / remainder indicators */}
      <div className="mt-3 rounded-lg border border-border bg-surface-raised/60 px-3 py-2 text-xs">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <span className="text-ink-muted">Sum of splits: </span>
            <span
              className={[
                'font-mono font-semibold',
                overCap ? 'text-neg' : 'text-ink',
              ].join(' ')}
            >
              {sumPct}%
            </span>
          </div>
          <div>
            <span className="text-ink-muted">Remainder to source: </span>
            <span
              className={[
                'font-mono font-semibold',
                overCap
                  ? 'text-neg'
                  : remainder === 0
                  ? 'text-ink-muted'
                  : 'text-brand',
              ].join(' ')}
            >
              {overCap ? '—' : `${remainder}%`}
            </span>
          </div>
        </div>
        {overCap && (
          <p className="mt-1 text-neg">
            Total exceeds 100%. Trim a row to save.
          </p>
        )}
      </div>

      {!readOnly && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button size="sm" variant="ghost" onClick={addRow}>
            + Add split
          </Button>
          <div className="flex-1" />
          <Button
            size="sm"
            variant="ghost"
            onClick={onCancel}
            disabled={!hasChanges || saveMut.isPending}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={onSave}
            disabled={saveMut.isPending || overCap}
          >
            {saveMut.isPending ? <Spinner /> : 'Save splits'}
          </Button>
        </div>
      )}

      {localError && (
        <div className="mt-3">
          <ErrorBanner>{localError}</ErrorBanner>
        </div>
      )}
    </Card>
  );
}

function SplitRow({
  index,
  total,
  draft,
  targetOptions,
  readOnly,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
}: {
  index: number;
  total: number;
  draft: DraftSplit;
  targetOptions: { value: string; label: string }[];
  readOnly: boolean;
  onChange: (patch: Partial<DraftSplit>) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}) {
  const firstRow = index === 0;
  const lastRow = index === total - 1;

  return (
    <div className="rounded-lg border border-border bg-surface-raised/40 p-3">
      {/* Responsive grid: single column on mobile, 3-col on sm+ */}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1.2fr_1.4fr_0.8fr]">
        <TextInput
          label={index === 0 ? 'Label' : undefined}
          value={draft.label}
          onChange={(e) => onChange({ label: e.target.value })}
          placeholder="e.g. paati"
          disabled={readOnly}
        />
        <Select
          label={index === 0 ? 'Target account' : undefined}
          value={
            draft.targetMatchAccountId === null
              ? ''
              : String(draft.targetMatchAccountId)
          }
          onChange={(v) =>
            onChange({
              targetMatchAccountId: v === '' ? null : Number(v),
            })
          }
          options={targetOptions}
        />
        <TextInput
          label={index === 0 ? 'Percentage' : undefined}
          value={draft.percentage}
          onChange={(e) => onChange({ percentage: e.target.value })}
          type="number"
          inputMode="decimal"
          placeholder="e.g. 30"
          disabled={readOnly}
          hint={index === 0 ? '0 < % ≤ 100' : undefined}
        />
      </div>

      {!readOnly && (
        <div className="mt-2 flex items-center justify-between gap-2">
          <span className="text-[11px] text-ink-muted">
            Row {index + 1} of {total}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={onMoveUp}
              disabled={firstRow}
              aria-label="Move up"
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-surface-raised text-ink transition-colors hover:bg-surface-raised disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ArrowUp className="h-4 w-4" strokeWidth={2} />
            </button>
            <button
              type="button"
              onClick={onMoveDown}
              disabled={lastRow}
              aria-label="Move down"
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-surface-raised text-ink transition-colors hover:bg-surface-raised disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ArrowDown className="h-4 w-4" strokeWidth={2} />
            </button>
            <button
              type="button"
              onClick={onRemove}
              aria-label="Delete row"
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-surface-raised text-neg transition-colors hover:bg-neg/5 hover:text-neg"
            >
              <Trash2 className="h-4 w-4" strokeWidth={2} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Recent activity
// ---------------------------------------------------------------------------

function RecentActivity({
  rows,
  loading,
}: {
  rows: MatchLedgerRow[];
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 py-4 text-xs text-ink-muted">
        <Spinner /> Loading recent activity…
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <p className="py-4 text-center text-xs text-ink-muted">
        No ledger entries yet.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-xs">
        <thead>
          <tr className="border-b border-border text-left text-[10px] uppercase tracking-wide text-ink-muted">
            <th className="px-2 py-2">Date</th>
            <th className="px-2 py-2">Label / kind</th>
            <th className="px-2 py-2 text-right">Amount</th>
            <th className="px-2 py-2">Note</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((r) => {
            const voided = r.voided_at !== null;
            return (
              <tr
                key={r.id}
                className={voided ? 'text-ink-muted line-through' : ''}
              >
                <td className="px-2 py-1.5 font-mono text-ink-muted">
                  {r.effective_date}
                </td>
                <td className="px-2 py-1.5">
                  <span className="text-ink">
                    {r.label ?? (
                      <span className="italic text-ink-muted">
                        remainder
                      </span>
                    )}
                  </span>
                  <span className="ml-2 text-[10px] uppercase text-ink-muted">
                    {r.part_kind}
                  </span>
                </td>
                <td
                  className={[
                    'px-2 py-1.5 text-right font-mono',
                    Number(r.amount) >= 0
                      ? 'text-pos'
                      : 'text-neg',
                  ].join(' ')}
                >
                  {r.amount}
                </td>
                <td className="px-2 py-1.5 text-ink-muted">
                  {r.note ?? '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
